import { jwtDecode } from 'jwt-decode';
import type { AccessTokenPayload } from './authTypes';

/**
 * Access token lives in memory only (RAM) — never in localStorage/sessionStorage.
 * It is intentionally lost on full page reload; the seawall `jwt` HttpOnly
 * refresh cookie is what restores the session (see authApi.refreshRequest).
 */
class TokenStorage {
  private accessToken: string | null = null;
  private payload: AccessTokenPayload | null = null;

  setToken(token: string, payload: AccessTokenPayload) {
    this.accessToken = token;
    this.payload = payload;
  }

  getToken() {
    return this.accessToken;
  }

  getPayload() {
    return this.payload;
  }

  isTokenActive() {
    if (!this.accessToken || !this.payload?.exp) return false;
    return Date.now() < this.payload.exp * 1000;
  }

  clearToken() {
    this.accessToken = null;
    this.payload = null;
  }
}

export const tokenStorage = new TokenStorage();

export function decodeAccessToken(token: string): AccessTokenPayload {
  return jwtDecode<AccessTokenPayload>(token);
}

/** Decode + store a fresh access token in one step; returns the decoded claims. */
export function storeAccessToken(token: string): AccessTokenPayload {
  const payload = decodeAccessToken(token);
  tokenStorage.setToken(token, payload);
  return payload;
}
